import { TwirpFetchTransport } from '@protobuf-ts/twirp-transport'
import { DocumentsClient, MetricsClient } from '@ttab/elephant-api/repository'
import type {
  BulkGetResponse,
  BulkUpdateResponse,
  GetDocumentResponse,
  GetMetaResponse,
  GetStatusOverviewResponse,
  UpdateRequest,
  UpdateResponse,
  ValidateRequest,
  ValidateResponse,
  GetStatusHistoryReponse,
  GetMetricsResponse,
  AttachmentDetails,
  StatusUpdate,
  GetHistoryResponse,
  ValidationResult,
  GetDeliverableInfoResponse
} from '@ttab/elephant-api/repository'
import { Block, Document } from '@ttab/elephant-api/newsdoc'
import type { RpcError, FinishedUnaryCall } from '@protobuf-ts/runtime-rpc'
import type * as Y from 'yjs'
import { isValidUUID } from './isValidUUID.js'
import { batched } from './batched.js'
import { fromYjsNewsDoc } from '@/shared/transformations/yjsNewsDoc.js'
import { fromGroupedNewsDoc } from '@/shared/transformations/groupedNewsDoc.js'

import { decodeJwt } from 'jose'
import { meta } from './meta.js'
import { getSystemLanguage } from '@/shared/getSystemLanguage.js'

function buildAcl(accessToken: string): Array<{ uri: string, permissions: string[] }> {
  try {
    const decoded = decodeJwt(accessToken)
    const units = Array.isArray(decoded.units) ? decoded.units as string[] : []

    if (units.includes('/redaktionen-npk')) {
      return [{ uri: 'core://unit/redaktionen-npk', permissions: ['r', 'w'] }]
    }
  } catch {
    // JWT decode failure → fall back to default ACL
  }

  return [{ uri: 'core://unit/redaktionen', permissions: ['r', 'w'] }]
}

// Caps confirmed by the repository's own invalid_argument responses. Requests
// above them are split by `batched`, so callers never have to know a cap exists.
const BULK_GET_BATCH_SIZE = 200
const STATUS_UUID_BATCH_SIZE = 200

// No cap observed here - getMetrics accepted 226 uuids. Matched to the confirmed
// caps rather than to a measured limit.
const METRICS_BATCH_SIZE = 200

export interface Status {
  name: string
  version: bigint
  uuid: string
  checkpoint?: string
  cause?: string
  type?: string
  usableId?: bigint
}

export class Repository {
  readonly #client: DocumentsClient
  readonly #metricsClient: MetricsClient

  constructor(repoUrl: string) {
    this.#client = new DocumentsClient(
      new TwirpFetchTransport({
        baseUrl: new URL('twirp', repoUrl).toString(),
        sendJson: true,
        jsonOptions: {
          ignoreUnknownFields: true
        }
      })
    )

    this.#metricsClient = new MetricsClient(
      new TwirpFetchTransport({
        baseUrl: new URL('twirp', repoUrl).toString(),
        sendJson: true,
        jsonOptions: {
          ignoreUnknownFields: true
        }
      })
    )
  }

  /**
   * Get the status documents for the given array of uuids from the respository.
   *
   * The uuids are deduplicated before being batched.
   *
   * @param options - { uuids: string[], statuses: string[], accessToken: string }
   * @returns Promise<GetStatusOverviewResponse | null>
   */
  async getStatuses({ uuids, statuses, accessToken }: {
    uuids: string[]
    statuses: string[]
    accessToken: string
  }): Promise<GetStatusOverviewResponse | null> {
    if (!uuids.length) {
      console.warn('no uuids to fetch statuses from')
      return { items: [] }
    }

    if (uuids.filter(isValidUUID).length !== uuids.length) {
      console.warn('number of valid uuids did not match')
      return { items: [] }
    }

    try {
      return await batched(
        [...new Set(uuids)],
        STATUS_UUID_BATCH_SIZE,
        async (batch) => {
          const { response } = await this.#client.getStatusOverview({
            statuses,
            uuids: batch,
            getMeta: false
          }, meta(accessToken))

          return response
        },
        (responses) => ({ items: responses.flatMap(({ items }) => items ?? []) })
      )
    } catch (err: unknown) {
      throw new Error(`Unable to fetch statuses: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }

  /**
   * Get the latest version of the documents specified by the given
   * array of uuids from the respository.
   *
   * @param options - { uuids: string[], accessToken: string }
   * @returns Promise<BulkGetResponse | null>
   */
  async getDocuments({ documents, accessToken, abort, subset = [] }: {
    documents: { uuid: string, version?: bigint }[]
    accessToken: string
    abort?: AbortSignal
    /** Newsdoc-extractor expressions. `[]` (default) returns the full document. */
    subset?: string[]
  }): Promise<BulkGetResponse | null> {
    if (!documents.length || !documents.filter((document) => isValidUUID(document.uuid)).length) {
      return null
    }

    // Filter before batching so the batches are cut from what is actually sent
    const requested = documents
      .filter((doc) => doc?.version !== -1n)
      .map((document) => ({ uuid: document.uuid, version: document.version || 0n }))

    try {
      return await batched(
        requested,
        BULK_GET_BATCH_SIZE,
        async (batch) => {
          const { response } = await this.#client.bulkGet({
            documents: batch,
            subset
          }, meta(accessToken, abort))

          return response
        },
        (responses) => ({ items: responses.flatMap(({ items }) => items ?? []) })
      )
    } catch (err: unknown) {
      throw new Error(`Unable to fetch documents in bulk: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }

  /**
   * Get a document from the repository.
   *
   * @param options - { uuids: string[], accessToken: string }
   * @returns Promise<GetDocumentResponse>
   */
  async getDocument({ uuid, accessToken, version }: { uuid: string, accessToken: string, version?: number }): Promise<GetDocumentResponse | null> {
    if (!isValidUUID(uuid)) {
      throw new Error('Invalid uuid format')
    }

    try {
      const { response } = await this.#client.get({
        uuid,
        version: version ? BigInt(version) : 0n,
        status: '',
        metaDocument: 1,
        metaDocumentVersion: 0n,
        subset: []
      }, meta(accessToken))

      return response
    } catch (err: unknown) {
      if ((err as { code: string })?.code === 'not_found') {
        return null
      }

      throw new Error(`Unable to fetch document: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }

  /**
   * Retrieves the meta information for the given UUID.
   *
   * @param {Object} params - The parameters for retrieving meta information.
   * @param {string} params.uuid - The UUID of the document.
   * @param {string} params.accessToken - The access token.
   * @returns {Promise<GetMetaResponse | null>} The meta information or null if not found.
   * @throws {Error} If the UUID format is invalid or unable to fetch meta information.
   */
  async getMeta({ uuid, accessToken }: { uuid: string, accessToken: string }): Promise<GetMetaResponse | null> {
    if (!isValidUUID(uuid)) {
      throw new Error('Invalid uuid format')
    }

    try {
      const { response } = await this.#client.getMeta({ uuid }, meta(accessToken))

      return response
    } catch (err) {
      if ((err as RpcError).code === 'not_found') {
        return null
      }

      throw new Error(`Unable to fetch documents meta: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }

  async getDeliverableInfo({ uuid, accessToken }: {
    uuid: string
    accessToken: string
  }): Promise<GetDeliverableInfoResponse | null> {
    if (!isValidUUID(uuid)) {
      return null
    }

    try {
      const { response } = await this.#client.getDeliverableInfo({ uuid }, meta(accessToken))
      return response
    } catch (err: unknown) {
      if ((err as { code: string })?.code === 'not_found') {
        return null
      }

      throw new Error(`Unable to fetch deliverable info: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }

  /**
   * Retrieves the history for the given UUID.
   *
   * @param {Object} params - The parameters for retrieving history.
   * @param {string} params.uuid - The UUID of the document.
   * @param {string} params.accessToken - The access token.
   * @param {AbortSignal} params.abort - The abort signal used to cancel the request.
   * @returns {Promise<GetHistoryResponse | null>} The history or null if not found.
   * @throws {Error} If the UUID format is invalid or unable to fetch history.
   */
  async getHistory({ uuid, accessToken, abort }: {
    uuid: string
    accessToken: string
    abort?: AbortSignal
  }): Promise<GetHistoryResponse | null> {
    if (!isValidUUID(uuid)) {
      throw new Error('Invalid uuid format')
    }

    try {
      const { response } = await this.#client.getHistory(
        {
          uuid,
          before: BigInt(0),
          loadStatuses: true
        },
        meta(accessToken, abort)
      )

      return response
    } catch (err) {
      if ((err as RpcError).code === 'not_found') {
        return null
      }

      throw new Error(`Unable to fetch document history: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }

  /**
   * Lists the document version history.
   *
   * @param {Object} params - The parameters for retrieving meta information.
   * @param {string} params.uuid - The UUID of the document.
   * @param {bigint} params.before - Which version number we should start fetching history from.
   * @param {string} params.accessToken - The access token.
   * @param {boolean} params.loadStatuses - Loads any statuses set on the document versions.
   * @returns {Promise<GetHistoryResponse | null>} The document history or null if not found.
   * @throws {Error} If the UUID format is invalid or unable to fetch the history.
   */
  async getStatusHistory({ uuid, accessToken }: { uuid: string, accessToken: string }): Promise<GetStatusHistoryReponse | null> {
    if (!isValidUUID(uuid)) {
      throw new Error('Invalid uuid format')
    }

    try {
      const { response } = await this.#client.getStatusHistory({ uuid, name: 'usable', before: 0n }, meta(accessToken))

      return response
    } catch (err) {
      throw new Error(`Unable to fetch document history: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }

  /**
   * Saves the meta information for the document.
   *
   * @param {Object} params - The parameters for saving meta information.
   * @param {Object} params.status - The status information.
   * @param {bigint} params.status.version - The version of the status.
   * @param {string} params.status.name - The name of the status.
   * @param {string} params.status.uuid - The UUID of the status.
   * @param {string} params.accessToken - The access token.
   * @param {string} params.currentStatus - The status information.
   * @returns {Promise<UpdateResponse>} The response from the update operation.
   * @throws {Error} If unable to save meta information.
   */
  async saveMeta({ status, accessToken, cause, isWorkflow = false, currentStatus }: {
    status: Status
    currentStatus?: Status
    accessToken: string
    cause?: string
    isWorkflow?: boolean
  }): Promise<UpdateResponse> {
    try {
      const { response } = await this.#client.update({
        uuid: status.uuid,
        status: (status.name === 'draft' && !isWorkflow)
          ? [
              { name: 'read', version: -1n, meta: {}, ifMatch: -1n },
              { name: 'saved', version: -1n, meta: {}, ifMatch: -1n },
              { name: 'used', version: -1n, meta: {}, ifMatch: -1n }
            ]
          : [{
              name: status.name,
              version: status.version,
              meta: cause ? { cause } : {},
              ifMatch: status.version
            }],
        meta: {},
        ifMatch: status.version < 0 ? currentStatus?.version ? currentStatus.version : status.version : status.version,
        acl: [],
        updateMetaDocument: false,
        lockToken: '',
        ifWorkflowState: '',
        ifStatusHeads: {},
        attachObjects: {},
        detachObjects: []
      }, meta(accessToken))

      return response
    } catch (err: unknown) {
      throw new Error(`Unable to save meta: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }

  /**
   * Saves meta information for multiple documents in a single bulk request.
   *
   * @param {Object} params - The parameters for saving meta information.
   * @param {Status[]} params.statuses - Array of status objects to update.
   * @param {string} params.accessToken - The access token.
   * @returns {Promise<BulkUpdateResponse>} The responses for each update.
   * @throws {Error} If unable to bulk save meta information.
   */
  async bulkSaveMeta({ statuses, accessToken }: {
    statuses: Status[]
    accessToken: string
  }): Promise<BulkUpdateResponse> {
    try {
      const { response } = await this.#client.bulkUpdate({
        updates: statuses.map((status) => ({
          uuid: status.uuid,
          status: status.name === 'draft'
            ? [
                { name: 'read', version: -1n, meta: {}, ifMatch: -1n },
                { name: 'saved', version: -1n, meta: {}, ifMatch: -1n },
                { name: 'used', version: -1n, meta: {}, ifMatch: -1n }
              ]
            : [{
                name: status.name,
                version: status.version,
                meta: {},
                ifMatch: status.version
              }],
          meta: {},
          ifMatch: status.version,
          acl: [],
          updateMetaDocument: false,
          lockToken: '',
          ifWorkflowState: '',
          ifStatusHeads: {},
          attachObjects: {},
          detachObjects: []
        }))
      }, meta(accessToken))

      return response
    } catch (err: unknown) {
      throw new Error(`Unable to bulk save meta: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }

  /**
   * Atomically create a new document, optionally a companion planning, and
   * optionally update the source document's status. Used for article type
   * conversion — the timeless→article direction passes a newPlanning (to own
   * the new article as a deliverable) and marks the source timeless as
   * "used"; the reverse direction omits both because the article workflow
   * doesn't carry a "used" status.
   */
  async createDerivedDocument({
    newDocument,
    newPlanning,
    sourceStatusUpdate,
    accessToken
  }: {
    newDocument: Document
    newPlanning?: Document
    sourceStatusUpdate?: {
      uuid: string
      name: string
      version: bigint
    }
    accessToken: string
  }): Promise<BulkUpdateResponse> {
    const createUpdate = (doc: Document) => ({
      uuid: doc.uuid,
      document: doc,
      meta: {},
      ifMatch: -1n, // Only create if doesn't exist
      status: [],
      acl: buildAcl(accessToken),
      updateMetaDocument: false,
      lockToken: '',
      ifWorkflowState: '',
      ifStatusHeads: {},
      attachObjects: {},
      detachObjects: []
    })

    const statusUpdate = sourceStatusUpdate
      ? [{
          uuid: sourceStatusUpdate.uuid,
          meta: {},
          ifMatch: 0n, // No optimistic lock on status update
          status: [{
            name: sourceStatusUpdate.name,
            version: sourceStatusUpdate.version,
            meta: {},
            ifMatch: 0n
          }],
          acl: [],
          updateMetaDocument: false,
          lockToken: '',
          ifWorkflowState: '',
          ifStatusHeads: {},
          attachObjects: {},
          detachObjects: []
        }]
      : []

    try {
      const { response } = await this.#client.bulkUpdate({
        updates: [
          createUpdate(newDocument),
          ...(newPlanning ? [createUpdate(newPlanning)] : []),
          ...statusUpdate
        ]
      }, meta(accessToken))

      return response
    } catch (err: unknown) {
      throw new Error(`Unable to create derived document: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }

  /**
   * Save a newsdoc to repository
   *
   * @param yDoc Y.Doc
   * @param accessToken string
   * @returns Promise<FinishedUnaryCall<UpdateRequest, UpdateResponse>
   */
  async saveDocument(
    document: Document,
    accessToken: string,
    status?: string,
    cause?: string,
    attachObjects?: Record<string, string>
  ): Promise<FinishedUnaryCall<UpdateRequest, UpdateResponse> | undefined> {
    const basicStatus: StatusUpdate[] = [{
      name: status || '',
      // Use the resulting version from the save
      version: 0n,
      meta: cause ? { cause } : {},
      // No optimistic lock
      ifMatch: 0n
    }]

    const payload: UpdateRequest = {
      document,
      meta: {},
      // No optimistic lock
      ifMatch: 0n,
      status: ([
        'core/article',
        'tt/print-article',
        'core/flash',
        'core/editorial-info'
      ].includes(document.type))
        ? status
          ? basicStatus
          : []
        : status && status !== 'draft'
          ? basicStatus
          : [],
      acl: buildAcl(accessToken),
      uuid: document.uuid,
      lockToken: '',
      updateMetaDocument: false,
      ifWorkflowState: '',
      ifStatusHeads: {},
      attachObjects: attachObjects || {},
      detachObjects: []
    }

    return await this.#client.update(
      payload, meta(accessToken)
    )
  }

  async getMetrics(uuids: string[], kinds: string[], accessToken: string): Promise<GetMetricsResponse> {
    if (!uuids.length || uuids.filter(isValidUUID).length !== uuids.length) {
      throw new Error('Invalid uuid format in input')
    }

    try {
      return await batched(
        uuids,
        METRICS_BATCH_SIZE,
        async (batch) => {
          const { response } = await this.#metricsClient.getMetrics({
            uuids: batch,
            kinds
          }, meta(accessToken))

          return response
        },
        (responses) => ({
          documents: responses.reduce<GetMetricsResponse['documents']>(
            (all, { documents }) => ({ ...all, ...documents }), {}
          )
        })
      )
    } catch (err: unknown) {
      throw new Error(`Unable to fetch metrics: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }

  /**
  * Validate a newsdoc without writing to repository
  * @param yDoc Y.Doc
  * @returns Promise<FinishedUnaryCall<ValidateRequest, ValidateResponse>>
  */
  async validateDoc(ydoc: Y.Doc): Promise<FinishedUnaryCall<ValidateRequest, ValidateResponse>> {
    const documentResponse = fromYjsNewsDoc(ydoc)

    return await this.#client.validate(
      fromGroupedNewsDoc(documentResponse)
    )
  }

  /**
   * Upload a file to the repository and file storage.
   */
  async uploadFile(name: string, contentType: string, file: File | Blob, accessToken: string): Promise<{
    uuid: string
    name: string
    contentType: string
    url: string
    uploadId: string
  }> {
    const { response } = await this.#client.createUpload({
      name,
      contentType,
      meta: {}
    }, meta(accessToken))

    const { id: uploadId, url } = response || {}

    if (!url || !uploadId) {
      throw new Error('CreateUpload request did not return a signed upload url')
    }

    try {
      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': contentType
        },
        mode: 'cors'
      })
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text().catch(() => 'Unknown error')
        throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}. Response: ${errorText}`)
      }

      const uuid = crypto.randomUUID()

      const document = Document.create({
        uuid,
        uri: `core://image/${uuid}`,
        language: getSystemLanguage(),
        title: name,
        type: 'core/image',
        meta: [
          Block.create({
            type: 'core/image',
            data: {
              filename: name,
              mimetype: contentType
            }
          })
        ]
      })

      // Pass attachObjects object as last parameter, together with image upload id
      await this.saveDocument(document, accessToken, undefined, undefined, { image: uploadId })

      return {
        uuid,
        name,
        contentType,
        url: uploadResponse.url,
        uploadId
      }
    } catch (error) {
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        console.error('CORS error: The S3 bucket is not configured to allow requests from this origin. Please configure CORS policy on the S3 bucket.')
        throw new Error('Upload failed due to CORS policy. Please contact your administrator to configure the S3 bucket CORS settings.')
      }
      console.error('Upload error:', error)
      throw error
    }
  }

  /**
   * Get a signed download url to an uploaded file.
   */
  async getAttachmentDetails(docId: string, accessToken: string): Promise<AttachmentDetails> {
    const { response } = await this.#client.getAttachments({
      documents: [docId],
      attachmentName: 'image',
      downloadLink: true
    }, meta(accessToken))

    const { attachments } = response || {}
    if (!Array.isArray(attachments) || !attachments.length) {
      throw new Error('Get attachments request did not return a signed download url')
    }

    return attachments[0]
  }

  /**
   * Prune a document against its type schema, stripping fields that aren't
   * valid. Used for timeless→article conversion where the article schema is
   * stricter than timeless.
   */
  async pruneDocument(document: Document, accessToken: string): Promise<{
    document: Document
    errors: ValidationResult[]
  }> {
    try {
      const { response } = await this.#client.prune({ document }, meta(accessToken))
      if (!response.document) {
        throw new Error('Prune API returned empty document')
      }
      return {
        document: response.document,
        errors: response.errors ?? []
      }
    } catch (err: unknown) {
      throw new Error(`Unable to prune document: ${(err as Error)?.message || 'Unknown error'}`)
    }
  }
}
